// src/components/attributes/attribute-form-panel.tsx

/**
 * Attribute Form Panel
 * Slide-out panel for creating and editing attributes
 */

import { useState, useEffect } from 'react'
import { X } from 'lucide-react'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import type {
  Attribute,
  AttributeCategory,
  AttributeDataType,
  AttributeSourceType,
  CreateAttributeRequest,
  UpdateAttributeRequest,
  ValidationRules,
} from '@/types/attribute'
import {
  DATA_TYPE_LABELS,
  SOURCE_TYPE_LABELS,
} from '@/types/attribute'

interface AttributeFormPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  attribute: Attribute | null
  category: AttributeCategory
  onSubmit: (data: CreateAttributeRequest | UpdateAttributeRequest) => void
  isSubmitting?: boolean
}

const KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_.\-]*$/

export function AttributeFormPanel({
  open,
  onOpenChange,
  attribute,
  category,
  onSubmit,
  isSubmitting = false,
}: AttributeFormPanelProps) {
  const isEditMode = attribute !== null

  // Form state
  const [key, setKey] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [dataType, setDataType] = useState<AttributeDataType>('STRING')
  const [isMultiValue, setIsMultiValue] = useState(false)
  const [sourceType, setSourceType] = useState<AttributeSourceType>('MANUAL')
  const [validationEnabled, setValidationEnabled] = useState(false)

  // Validation rules state
  const [required, setRequired] = useState(false)
  const [regex, setRegex] = useState('')
  const [minValue, setMinValue] = useState('')
  const [maxValue, setMaxValue] = useState('')
  const [minLength, setMinLength] = useState('')
  const [maxLength, setMaxLength] = useState('')
  const [enumValues, setEnumValues] = useState<string[]>([])
  const [newEnumValue, setNewEnumValue] = useState('')

  // Errors
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Reset form when opening/closing or attribute changes
  useEffect(() => {
    if (open) {
      if (attribute) {
        setKey(attribute.key)
        setDisplayName(attribute.displayName)
        setDescription(attribute.description || '')
        setDataType(attribute.dataType)
        setIsMultiValue(attribute.isMultiValue)
        setSourceType(attribute.sourceType)
        setValidationEnabled(attribute.validationEnabled)

        const rules = attribute.validationRules
        if (rules) {
          setRequired(rules.required || false)
          setRegex(rules.regex || '')
          setMinValue(rules.minValue?.toString() || '')
          setMaxValue(rules.maxValue?.toString() || '')
          setMinLength(rules.minLength?.toString() || '')
          setMaxLength(rules.maxLength?.toString() || '')
          setEnumValues(rules.enumValues || [])
        }
      } else {
        // Reset for new attribute
        setKey('')
        setDisplayName('')
        setDescription('')
        setDataType('STRING')
        setIsMultiValue(false)
        setSourceType('MANUAL')
        setValidationEnabled(false)
        setRequired(false)
        setRegex('')
        setMinValue('')
        setMaxValue('')
        setMinLength('')
        setMaxLength('')
        setEnumValues([])
      }
      setErrors({})
      setNewEnumValue('')
    }
  }, [open, attribute])

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!key.trim()) {
      newErrors.key = 'Key is required'
    } else if (!KEY_PATTERN.test(key)) {
      newErrors.key = 'Key must start with a letter and contain only letters, numbers, dots, hyphens, and underscores'
    }

    if (!displayName.trim()) {
      newErrors.displayName = 'Display name is required'
    } else if (displayName.length > 255) {
      newErrors.displayName = 'Display name must be 255 characters or less'
    }

    if (description.length > 2000) {
      newErrors.description = 'Description must be 2000 characters or less'
    }

    if (dataType === 'ENUM' && enumValues.length === 0) {
      newErrors.enumValues = 'At least one enum value is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = () => {
    if (!validate()) return

    // Build validation rules
    let validationRules: ValidationRules | undefined
    if (validationEnabled) {
      validationRules = { required }

      if (dataType === 'STRING') {
        if (regex) validationRules.regex = regex
        if (minLength) validationRules.minLength = parseInt(minLength, 10)
        if (maxLength) validationRules.maxLength = parseInt(maxLength, 10)
      }

      if (dataType === 'INTEGER' || dataType === 'DOUBLE') {
        if (minValue) validationRules.minValue = parseFloat(minValue)
        if (maxValue) validationRules.maxValue = parseFloat(maxValue)
      }

      if (dataType === 'ENUM') {
        validationRules.enumValues = enumValues
      }
    }

    if (isEditMode) {
      const updateData: UpdateAttributeRequest = {
        displayName,
        description: description || undefined,
        dataType,
        isMultiValue,
        sourceType,
        validationEnabled,
        validationRules,
      }
      onSubmit(updateData)
    } else {
      const createData: CreateAttributeRequest = {
        key,
        displayName,
        description: description || undefined,
        category,
        dataType,
        isMultiValue,
        sourceType,
        validationEnabled,
        validationRules,
      }
      onSubmit(createData)
    }
  }

  // Add enum value
  const handleAddEnumValue = () => {
    const value = newEnumValue.trim()
    if (value && !enumValues.includes(value)) {
      setEnumValues([...enumValues, value])
      setNewEnumValue('')
      if (errors.enumValues) {
        setErrors((prev) => ({ ...prev, enumValues: '' }))
      }
    }
  }

  // Remove enum value
  const handleRemoveEnumValue = (value: string) => {
    setEnumValues(enumValues.filter((v) => v !== value))
  }

  // Show validation fields based on data type
  const showStringValidation = dataType === 'STRING'
  const showNumberValidation = dataType === 'INTEGER' || dataType === 'DOUBLE'
  const showEnumValidation = dataType === 'ENUM'

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {isEditMode ? 'Edit Attribute' : 'New Attribute'}
          </SheetTitle>
          <SheetDescription>
            {isEditMode
              ? 'Update the attribute definition'
              : `Create a new ${category.toLowerCase()} attribute`}
          </SheetDescription>
        </SheetHeader>

        <div className="grid gap-4 py-4">
          {/* Key */}
          <div className="grid gap-2">
            <Label htmlFor="key">
              Key <span className="text-destructive">*</span>
            </Label>
            <Input
              id="key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="e.g., user.role"
              disabled={isEditMode || isSubmitting}
              className="font-mono"
            />
            {errors.key && (
              <p className="text-sm text-destructive">{errors.key}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Unique identifier for this attribute
            </p>
          </div>

          {/* Display Name */}
          <div className="grid gap-2">
            <Label htmlFor="displayName">
              Display Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., User Role"
              disabled={isSubmitting}
            />
            {errors.displayName && (
              <p className="text-sm text-destructive">{errors.displayName}</p>
            )}
          </div>

          {/* Description */}
          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              disabled={isSubmitting}
              rows={2}
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {description.length}/2000 characters
            </p>
          </div>

          {/* Data Type */}
          <div className="grid gap-2">
            <Label htmlFor="dataType">
              Data Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={dataType}
              onValueChange={(value) => setDataType(value as AttributeDataType)}
              disabled={isSubmitting}
            >
              <SelectTrigger id="dataType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DATA_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Source Type */}
          <div className="grid gap-2">
            <Label htmlFor="sourceType">Source</Label>
            <Select
              value={sourceType}
              onValueChange={(value) => setSourceType(value as AttributeSourceType)}
              disabled={isSubmitting}
            >
              <SelectTrigger id="sourceType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SOURCE_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Multi-Value */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isMultiValue"
              checked={isMultiValue}
              onCheckedChange={(checked) => setIsMultiValue(checked === true)}
              disabled={isSubmitting}
            />
            <Label htmlFor="isMultiValue" className="font-normal">
              Allow multiple values
            </Label>
          </div>

          {/* Validation Enabled */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="validationEnabled"
              checked={validationEnabled}
              onCheckedChange={(checked) => setValidationEnabled(checked === true)}
              disabled={isSubmitting}
            />
            <Label htmlFor="validationEnabled" className="font-normal">
              Enable validation
            </Label>
          </div>

          {/* Conditional Validation Rules */}
          {validationEnabled && (
            <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
              <p className="text-sm font-medium">Validation Rules</p>

              {/* Required */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="required"
                  checked={required}
                  onCheckedChange={(checked) => setRequired(checked === true)}
                  disabled={isSubmitting}
                />
                <Label htmlFor="required" className="font-normal">
                  Required
                </Label>
              </div>

              {/* String validation */}
              {showStringValidation && (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="regex">Regex Pattern</Label>
                    <Input
                      id="regex"
                      value={regex}
                      onChange={(e) => setRegex(e.target.value)}
                      placeholder="e.g., ^[A-Z]+$"
                      disabled={isSubmitting}
                      className="font-mono"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="minLength">Min Length</Label>
                      <Input
                        id="minLength"
                        type="number"
                        value={minLength}
                        onChange={(e) => setMinLength(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="maxLength">Max Length</Label>
                      <Input
                        id="maxLength"
                        type="number"
                        value={maxLength}
                        onChange={(e) => setMaxLength(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Number validation */}
              {showNumberValidation && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="minValue">Min Value</Label>
                    <Input
                      id="minValue"
                      type="number"
                      value={minValue}
                      onChange={(e) => setMinValue(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="maxValue">Max Value</Label>
                    <Input
                      id="maxValue"
                      type="number"
                      value={maxValue}
                      onChange={(e) => setMaxValue(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              )}

              {/* Enum values */}
              {showEnumValidation && (
                <div className="grid gap-2">
                  <Label>Allowed Values</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newEnumValue}
                      onChange={(e) => setNewEnumValue(e.target.value)}
                      placeholder="Add value..."
                      disabled={isSubmitting}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddEnumValue()
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleAddEnumValue}
                      disabled={isSubmitting}
                    >
                      Add
                    </Button>
                  </div>
                  {enumValues.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {enumValues.map((value) => (
                        <Badge
                          key={value}
                          variant="secondary"
                          className="gap-1"
                        >
                          {value}
                          <button
                            type="button"
                            onClick={() => handleRemoveEnumValue(value)}
                            className="ml-1 hover:text-destructive"
                            disabled={isSubmitting}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  {errors.enumValues && (
                    <p className="text-sm text-destructive">{errors.enumValues}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <SheetFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? 'Saving...'
              : isEditMode
                ? 'Save Changes'
                : 'Create Attribute'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
